import type {
  InventoryDashboardRecentActivityRow,
  InventoryStockMovementRecord,
} from '@/renderer/types/inventory';
import { formatInventoryStockMovementType } from './stockMovementForm';

type Tone = {
  badgeClassName: string;
  quantityClassName: string;
};

const DEFAULT_TONE: Tone = {
  badgeClassName: 'bg-slate-100 text-slate-700',
  quantityClassName: 'text-slate-700',
};

const MOVEMENT_TONES: Record<string, Tone> = {
  purchase_receipt: {
    badgeClassName: 'bg-emerald-100 text-emerald-700',
    quantityClassName: 'text-emerald-700',
  },
  transfer_in: {
    badgeClassName: 'bg-sky-100 text-sky-700',
    quantityClassName: 'text-sky-700',
  },
  transfer_out: {
    badgeClassName: 'bg-orange-100 text-orange-700',
    quantityClassName: 'text-orange-700',
  },
  sale_consumption: {
    badgeClassName: 'bg-amber-100 text-amber-700',
    quantityClassName: 'text-amber-700',
  },
  adjustment_increase: {
    badgeClassName: 'bg-lime-100 text-lime-700',
    quantityClassName: 'text-lime-700',
  },
  adjustment_decrease: {
    badgeClassName: 'bg-rose-100 text-rose-700',
    quantityClassName: 'text-rose-700',
  },
  wastage: {
    badgeClassName: 'bg-rose-100 text-rose-700',
    quantityClassName: 'text-rose-700',
  },
  return_to_supplier: {
    badgeClassName: 'bg-fuchsia-100 text-fuchsia-700',
    quantityClassName: 'text-fuchsia-700',
  },
  stock_count_correction: {
    badgeClassName: 'bg-violet-100 text-violet-700',
    quantityClassName: 'text-violet-700',
  },
};

export function getMovementTone(movementType: string): Tone {
  return MOVEMENT_TONES[movementType] ?? DEFAULT_TONE;
}

export function getMovementBadgeLabel(movementType: string) {
  return formatInventoryStockMovementType(
    movementType as InventoryStockMovementRecord['movement_type'],
  );
}

export function getRecentActivityTone(
  row: InventoryDashboardRecentActivityRow,
): Tone {
  if (row.activity_type === 'supplier_invoice') {
    return {
      badgeClassName: 'bg-indigo-100 text-indigo-700',
      quantityClassName: 'text-indigo-700',
    };
  }

  if (row.activity_type === 'supplier_payment') {
    return {
      badgeClassName: 'bg-teal-100 text-teal-700',
      quantityClassName: 'text-teal-700',
    };
  }

  return getMovementTone(row.reference_type ?? 'stock_movement');
}

export function getRecentActivityLabel(
  row: InventoryDashboardRecentActivityRow,
) {
  if (row.activity_type === 'supplier_invoice') {
    return 'Supplier invoice';
  }

  if (row.activity_type === 'supplier_payment') {
    return 'Supplier payment';
  }

  return getMovementBadgeLabel(row.reference_type ?? 'stock_movement');
}

export function getRecentActivitySummaryTone(activityType: string): Tone {
  if (activityType === 'supplier_invoice') {
    return {
      badgeClassName: 'bg-indigo-100 text-indigo-700',
      quantityClassName: 'text-indigo-700',
    };
  }

  if (activityType === 'supplier_payment') {
    return {
      badgeClassName: 'bg-teal-100 text-teal-700',
      quantityClassName: 'text-teal-700',
    };
  }

  return {
    badgeClassName: 'bg-pos-primary/10 text-pos-primary',
    quantityClassName: 'text-pos-primary',
  };
}
